import { mediaQueryLarge, requestIdleCallback, startViewTransition } from "@theme/utilities";
import PaginatedList from "@theme/paginated-list";

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {(() => void) | undefined} */
  #loaderCleanup;
  /** @type {boolean} */
  #allLoaded = false;
  /** @type {number} Maximum number of pages to load (prevents excessive loading) */
  #maxPages = 50;
  /** @type {number} Last scroll position for velocity calculation */
  #lastScrollY = 0;
  /** @type {number} Last scroll time for velocity calculation */
  #lastScrollTime = Date.now();
  /** @type {ReturnType<typeof requestAnimationFrame> | undefined} */
  #scrollRafId;

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange);
    this.setAttribute("initialized", "");
    // Initialize scroll tracking
    this.#lastScrollY = window.scrollY;
    this.#lastScrollTime = Date.now();
    this.#setupGlobalLoader();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener("change", this.#handleMediaQueryChange);
    this.#loaderCleanup?.();
  }

  /**
   * Sets up the bottom loader to show during infinite scroll
   */
  #setupGlobalLoader() {
    const { grid } = this.refs;
    if (!grid) return;

    // Create loader element at bottom of container
    const loader = document.createElement("div");
    loader.className = "results-list-loader";
    loader.style.display = "none";
    loader.innerHTML = '<div class="results-list-loader__spinner"></div>';

    // Insert loader after grid or before viewMoreNext
    const viewMoreNext = this.querySelector('[ref="viewMoreNext"]');
    if (viewMoreNext && viewMoreNext.parentNode) {
      viewMoreNext.parentNode.insertBefore(loader, viewMoreNext);
    } else if (grid.parentNode) {
      grid.parentNode.appendChild(loader);
    } else {
      this.appendChild(loader);
    }

    let isLoading = false;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let safetyTimeout;
    /** @type {IntersectionObserver | undefined} */
    let triggerObserver;

    const getHighestPage = () => {
      let highest = 1;
      grid.querySelectorAll("[data-page]").forEach((item) => {
        if (item instanceof HTMLElement) {
          const page = parseInt(item.dataset.page || "1");
          if (page > highest) highest = page;
        }
      });
      return highest;
    };

    const hasMorePages = () => {
      const lastPage = parseInt(grid.dataset.lastPage || "1");
      const highestPage = getHighestPage();
      // Check both lastPage limit and maxPages limit
      return highestPage < lastPage && highestPage < this.#maxPages;
    };

    const hideLoader = () => {
      if (!isLoading) return;
      if (safetyTimeout) clearTimeout(safetyTimeout);
      isLoading = false;
      loader.style.display = "none";
    };

    const markComplete = () => {
      this.#allLoaded = true;
      hideLoader();
      if (triggerObserver) {
        triggerObserver.disconnect();
        triggerObserver = undefined;
      }
    };

    /**
     * Calculates dynamic rootMargin based on viewport height for better fast-scroll detection
     * Uses 200% of viewport height to ensure we catch fast scrolling
     */
    const getDynamicRootMargin = () => {
      const viewportHeight = window.innerHeight;
      // Use 200% of viewport height, with minimum of 300px and maximum of 1000px
      const margin = Math.min(Math.max(viewportHeight * 2, 300), 1000);
      return `${margin}px`;
    };

    /**
     * Checks if viewMoreNext is near viewport (for fast scroll detection)
     * @param {HTMLElement | null} element
     */
    const isNearViewport = (element) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      // Check if element is within 200% of viewport height from bottom
      return rect.top <= viewportHeight * 2;
    };

    /**
     * Triggers page load if conditions are met
     */
    const triggerLoad = () => {
      if (this.#allLoaded || isLoading) return;

      const viewMoreNext = this.querySelector('[ref="viewMoreNext"]');
      if (!viewMoreNext || !(viewMoreNext instanceof HTMLElement) || !hasMorePages()) {
        markComplete();
        return;
      }

      if (!isNearViewport(viewMoreNext)) return;

      isLoading = true;
      loader.style.display = "flex";

      safetyTimeout = setTimeout(() => {
        if (isLoading) {
          hasMorePages() ? hideLoader() : markComplete();
        }
      }, 3000); // Increased timeout for slower connections
    };

    const setupObserver = () => {
      if (this.#allLoaded) return;

      const viewMoreNext = this.querySelector('[ref="viewMoreNext"]');
      if (!viewMoreNext || !hasMorePages()) {
        markComplete();
        return;
      }

      if (triggerObserver) triggerObserver.disconnect();

      // Use dynamic rootMargin for better fast-scroll detection
      const rootMargin = getDynamicRootMargin();

      triggerObserver = new IntersectionObserver(
        (entries) => {
          if (this.#allLoaded || isLoading || !entries[0]?.isIntersecting) return;

          if (!hasMorePages() || !this.querySelector('[ref="viewMoreNext"]')) {
            markComplete();
            return;
          }

          isLoading = true;
          loader.style.display = "flex";

          safetyTimeout = setTimeout(() => {
            if (isLoading) {
              hasMorePages() ? hideLoader() : markComplete();
            }
          }, 3000); // Increased timeout for slower connections
        },
        { rootMargin }
      );

      triggerObserver.observe(viewMoreNext);
    };

    setupObserver();

    // Scroll event listener as fallback for fast scrolling
    // This catches cases where IntersectionObserver might miss due to very fast scrolling
    const handleScroll = () => {
      if (this.#allLoaded || isLoading) return;

      // Use requestAnimationFrame for performance
      if (this.#scrollRafId) {
        cancelAnimationFrame(this.#scrollRafId);
      }

      this.#scrollRafId = requestAnimationFrame(() => {
        // Calculate scroll velocity
        const currentScrollY = window.scrollY;
        const currentTime = Date.now();
        const timeDiff = currentTime - this.#lastScrollTime;
        const scrollDiff = Math.abs(currentScrollY - this.#lastScrollY);

        // Update last values
        this.#lastScrollY = currentScrollY;
        this.#lastScrollTime = currentTime;

        // Only check if scrolling fast (more than 100px per second) or if we're near the bottom
        // scrollVelocity is in pixels per millisecond, so 0.1 = 100px per second
        const scrollVelocity = timeDiff > 0 ? scrollDiff / timeDiff : 0;
        const nearBottom = window.innerHeight + currentScrollY >= document.documentElement.scrollHeight - 500;

        // Trigger if scrolling fast (100px/sec) or near bottom (within 500px)
        if (scrollVelocity > 0.1 || nearBottom) {
          triggerLoad();
        }
      });
    };

    // Throttled scroll listener (only active when not all loaded)
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let scrollTimeout;
    const throttledScrollHandler = () => {
      if (this.#allLoaded) return;

      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        handleScroll();
      }, 100); // Throttle to every 100ms
    };

    window.addEventListener("scroll", throttledScrollHandler, { passive: true });

    const gridObserver = new MutationObserver(() => {
      if (this.#allLoaded) return;

      const viewMoreNext = this.querySelector('[ref="viewMoreNext"]');

      // Check if items were added and hide loader
      if (isLoading && grid.querySelector(".ud-product-grid__item, .ud-collection-card")) {
        hideLoader();
        setTimeout(() => !hasMorePages() && markComplete(), 100);
      }

      // Handle viewMoreNext state
      if (!viewMoreNext) {
        markComplete();
      } else if (!triggerObserver) {
        setupObserver();
      }
    });

    gridObserver.observe(this, { childList: true, subtree: true });

    this.#loaderCleanup = () => {
      if (triggerObserver) triggerObserver.disconnect();
      if (safetyTimeout) clearTimeout(safetyTimeout);
      if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      window.removeEventListener("scroll", throttledScrollHandler);
      gridObserver.disconnect();
      loader.remove();
    };
  }

  /**
   * Updates the layout.
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (target instanceof HTMLInputElement) {
      this.#animateLayoutChange(target.value);
    }
  }

  /**
   * Sets the layout with animation.
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ["product-grid"]);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? "desktop" : "mobile";
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Sets the layout attribute.
   * @param {string} value
   */
  #setLayout(value) {
    this.refs.grid?.setAttribute("product-grid-view", value);
  }

  /**
   * Handles media query changes.
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const selector = event.matches
      ? '[data-grid-layout="desktop-default-option"]'
      : '[data-grid-layout="mobile-option"]';
    const targetElement = this.querySelector(selector);

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#setLayout("default");
    }
  };
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}
